import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";

import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useToast } from "../hooks/use-toast";
import { fetchProjects, fetchTags, fetchEntries, createEntry, updateEntry as apiUpdateEntry, deleteEntry as apiDeleteEntry, fetchUsersLite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Play, Square, Plus, Tag, Trash2, Download } from "lucide-react";
import { todayISO, ndaysAgoISO } from "../lib/dates";

function roundToTenthHours(decimalHours) { return Math.round(decimalHours * 10) / 10; }
function sumDecimal(arr) { return Number(arr.reduce((a, b) => a + (Number(b) || 0), 0).toFixed(1)); }
function monthBounds(ym) {
  // ym like "2025-10" -> { from: "2025-10-01", to: "2025-10-31" }
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, "0");
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2,"0")}` };
}

export default function TrackPage() {
  const { toast } = useToast();
  const { user } = useAuth();

  const [entries, setEntries] = useState([]);
  const [matters, setMatters] = useState([]);
  const [taskCodes, setTaskCodes] = useState([]);
  const [userMap, setUserMap] = useState({});

  const [running, setRunning] = useState(null); // { startTs, description, projectId, tagIds }
  const [tick, setTick] = useState(0);

  // prevent auth refresh timeout while timer running, up to 12 hours
  useEffect(() => { if (running) { window.__hourslaw_timerActive = true; } else { window.__hourslaw_timerActive = false; } return () => { window.__hourslaw_timerActive = false; }; }, [running]);
  useEffect(() => { if (!running) return; const start = running.startTs; const id = setInterval(() => { const elapsedHrs = (Date.now() - start) / 3600000; if (elapsedHrs >= 12) { window.__hourslaw_timerActive = false; } }, 60000); return () => clearInterval(id); }, [running]);

  // capture inputs for new entries
  const [desc, setDesc] = useState("");
  const [projectId, setProjectId] = useState("");
  
  const [selectedTagId, setSelectedTagId] = useState("");
  const [quickHours, setQuickHours] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayISO());

  // filters
  const [range, setRange] = useState("14");
  const [jumpMonth, setJumpMonth] = useState(""); // YYYY-MM; when set, overrides range
  const [fProject, setFProject] = useState("all");
  const [fTag, setFTag] = useState("all");
  
  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([fetchProjects(), fetchTags()]);
        setMatters(p); setTaskCodes(t);
        let u = [];
        const role = (user?.role)||'user';
        if (role === 'admin' || role === 'superadmin') {
          try { u = await fetchUsersLite(); } catch(_){ u = []; }
        } else if (user?.id) {
          u = [{ id: user.id, email: user.email, name: user.name }];
        }
        const map = {}; (u||[]).forEach(x => { map[x.id] = x.email || x.name || x.id; }); setUserMap(map);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // load entries when range changes
  useEffect(() => {
    (async () => {
      try {
        let from, to;
        if (jumpMonth) {
          ({ from, to } = monthBounds(jumpMonth));
        } else {
          from = range === "all" ? "1900-01-01" : ndaysAgoISO(parseInt(range, 10) - 1);
          to = todayISO();
        }
        const data = await fetchEntries({ from, to });
        setEntries(data);
      } catch (e) { console.error(e); }
    })();
  }, [range, jumpMonth]);

  useEffect(() => { if (!running) return; const id = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(id); }, [running]);

  const selectedTaskCode = taskCodes.find(t => t.id === selectedTagId);

  const elapsedDecimal = useMemo(() => { if (!running) return 0; const ms = Date.now() - running.startTs; const decimal = ms / 3600000; return Math.round(decimal * 100) / 100; }, [tick, running]);

  const mattersForClient = useMemo(() => matters, [matters]);

  async function startTimer() { if (running) return; setRunning({ startTs: Date.now(), description: desc, projectId, tagIds: selectedTagId ? [selectedTagId] : [] }); }

  async function stopTimer() {
    if (!running) return; const decimal = elapsedDecimal; const rounded = Math.max(0.1, roundToTenthHours(decimal));
    try { const body = { description: running.description || "Untitled", projectId: running.projectId || null, tagIds: running.tagIds || [], date: todayISO(), decimalHours: rounded }; const created = await createEntry(body); setEntries(prev => [created, ...prev]); toast({ title: "Entry added", description: `Saved ${rounded.toFixed(1)}h` }); } catch (e) { console.error(e); toast({ title: "Failed to save", description: "Please try again" }); } finally { setRunning(null); }
  }

  async function addQuickEntry(e) {
    e.preventDefault(); const value = parseFloat(String(quickHours).replace(/,/g, ".")); if (isNaN(value) || value <= 0) { toast({ title: "Enter a positive number", description: "Use tenths e.g. 1.2" }); return; }
    const rounded = roundToTenthHours(value);
    try { const future = selectedDate > todayISO(); if (future) { toast({ title: "Future date", description: "You're adding time to a future date.", }); } const body = { description: desc || "Untitled", projectId: projectId || null, tagIds: selectedTagId ? [selectedTagId] : [], date: selectedDate, decimalHours: rounded }; const created = await createEntry(body); setEntries(prev => [created, ...prev]); setQuickHours(""); toast({ title: "Entry added", description: `Added ${rounded.toFixed(1)}h` }); } catch (e) { console.error(e); toast({ title: "Failed to add block", description: "Please try again" }); }
  }

  async function updateEntry(id, patch) { try { const payload = { ...patch }; if (typeof patch.durationHours === "number") { payload.decimalHours = patch.durationHours; delete payload.durationHours; } const updated = await apiUpdateEntry(id, payload); setEntries((prev) => prev.map(e => e.id === id ? updated : e)); } catch (e) { console.error(e); toast({ title: "Update failed", description: "Please retry" }); } }

  async function deleteEntry(id) { try { await apiDeleteEntry(id); setEntries((prev) => prev.filter(e => e.id !== id)); } catch (e) { console.error(e); toast({ title: "Delete failed", description: "Please retry" }); } }

  const filtered = useMemo(() => { return entries.filter(e => { if (fProject !== "all" && (e.projectId || "") !== fProject) return false; if (fTag !== "all" && !(e.tagIds || []).includes(fTag)) return false;  return true; }); }, [entries, fProject, fTag]);

  // Superadmin-only pagination (25 per page) over individual entries.
  // Entries are already sorted by date desc from backend; we slice the flat list
  // before grouping so each page shows ~25 newest entries.
  const PAGE_SIZE = 25;
  const isSuper = (user?.role === 'superadmin');
  const [page, setPage] = useState(1);
  // Reset page when filters/range change
  useEffect(() => { setPage(1); }, [range, jumpMonth, fProject, fTag]);
  const totalPages = isSuper ? Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)) : 1;
  const pageStart = isSuper ? (page - 1) * PAGE_SIZE : 0;
  const pageEnd = isSuper ? pageStart + PAGE_SIZE : filtered.length;
  const pagedEntries = isSuper ? filtered.slice(pageStart, pageEnd) : filtered;

  const grouped = useMemo(() => { const map = new Map(); for (const e of pagedEntries) { if (!map.has(e.date)) map.set(e.date, []); map.get(e.date).push(e); } return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1)); }, [pagedEntries]);

  function exportCSV(){ const header = ["date","user_email","description","matter","task_codes","hours"]; const projMap = new Map(matters.map(p => [p.id, p.name])); const tagMap = new Map(taskCodes.map(t => [t.id, t.name])); const rows = filtered.map(e => { const proj = e.projectId ? (projMap.get(e.projectId) || "") : ""; const tnames = (e.tagIds||[]).map(tid => tagMap.get(tid) || "").filter(Boolean).join(";"); const email = userMap[e.userId] || (user && (user.email || user.name)) || ""; const cells = [e.date, email, e.description || "", proj, tnames, String((e.decimalHours ?? (e.durationTenths/10)).toFixed(1))]; return cells.map(c => `"${String(c).replace(/"/g,'""')}"`).join(","); }); const csv = [header.join(","), ...rows].join("\n"); const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `hourslaw_entries_${jumpMonth ? jumpMonth : (range === "all" ? "all" : range + "d")}.csv`; a.click(); URL.revokeObjectURL(url); }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* Filters */}
      <div className="mb-4 hidden md:flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Range</span>
          <Select value={range} onValueChange={(v) => { setRange(v); setJumpMonth(""); }} disabled={!!jumpMonth}>
            <SelectTrigger className="w-[140px]" data-testid="track-range-select"><SelectValue placeholder="Range"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Jump to month</span>
          <input
            type="month"
            value={jumpMonth}
            onChange={(e)=>setJumpMonth(e.target.value)}
            className="border rounded px-2 py-1 text-sm h-9 bg-background"
            data-testid="track-jump-month-input"
          />
          {jumpMonth && (
            <Button
              variant="ghost"
              size="sm"
              onClick={()=>setJumpMonth("")}
              className="h-8 px-2"
              data-testid="track-jump-month-clear"
              title="Clear month filter"
            >Clear</Button>
          )}
        </div>

        <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Matter</span>
          <Select value={fProject} onValueChange={setFProject}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="All matters"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All matters</SelectItem>
              {matters.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Task code</span>
          <Select value={fTag} onValueChange={setFTag}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="All task codes"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All task codes</SelectItem>
              {taskCodes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto">
          <Button variant="secondary" onClick={exportCSV} className="gap-2"><Download className="size-4"/> Export CSV</Button>
        </div>
      </div>

      {/* Timer / Quick add card (hidden on small; mini appears) */}
      <div className="rounded-lg border bg-card p-4 mb-6 hidden md:block">
        {/* Description row alone, larger */}
        <div className="mb-3">
          <DescriptionTextarea value={desc} onChange={setDesc} />
        </div>
        {/* Controls row */}
        <div className="flex flex-wrap md:items-center gap-3">

          <Select value={(projectId || "none")} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="Select Matter"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No matter</SelectItem>
              {mattersForClient.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={(selectedTagId || "none")} onValueChange={(v) => setSelectedTagId(v === "none" ? "" : v)}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Select Task Code"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No task code</SelectItem>
              {taskCodes.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-2xl font-semibold tabular-nums">{running ? `${elapsedDecimal.toFixed(2)}h` : "0.00h"}</div>
            {!running ? (<Button onClick={startTimer} className="gap-2 brand-red-bg text-white"><Play className="size-4"/> Start</Button>) : (<Button variant="destructive" onClick={stopTimer} className="gap-2"><Square className="size-4"/> Stop</Button>)}
          </div>
        </div>
        <form onSubmit={addQuickEntry} className="mt-3 flex items-center gap-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Date</div>
            <input type="date" value={selectedDate} onChange={(e)=>setSelectedDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          </div>
          <Input value={quickHours} onChange={(e) => setQuickHours(e.target.value)} placeholder="Enter hours in tenths e.g. 1.2" inputMode="decimal" className="w-56" />
          <Button type="submit" className="gap-2 brand-red-bg text-white"><Plus className="size-4"/> Add Time</Button>
          {selectedTaskCode && <Badge variant="secondary" className="inline-flex items-center gap-1"><Tag className="size-3"/> {selectedTaskCode.name}</Badge>}

      {/* Superadmin: show user email on entries */}
      {/* Desktop entries list */}

        </form>
      </div>

      {/* Entries grouped by date */}
      {grouped.map(([date, rows]) => {
        const total = sumDecimal(rows.map(r => r.decimalHours ?? (r.durationTenths / 10)));
        const userRole = user?.role || 'user';
        const canEdit = userRole === 'superadmin' || userRole === 'user' || userRole === 'admin'; // Users/admins edit own, superadmins edit all
        const canDelete = userRole === 'superadmin' || userRole === 'user' || userRole === 'admin'; // Users/admins delete own, superadmins delete all
        
        return (
          <div key={date} className="mb-8 hidden md:block">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold tracking-tight">{date}</h3>
              <div className="text-sm text-muted-foreground">
                Total: <span className="font-medium text-foreground">{total.toFixed(1)}h</span>
              </div>
            </div>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[24%]">User</TableHead>
                    <TableHead className="w-[34%]">Description</TableHead>
                    <TableHead className="w-[22%]">Matter</TableHead>
                    <TableHead className="w-[12%] text-right">Hours (0.1)</TableHead>
                    <TableHead className="w-[8%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const email = userMap[r.userId] || r.userEmail || r.user || r.userId || '—';
                    const isOwnEntry = r.userId === user?.id;
                    const canEditThisEntry = userRole === 'superadmin' || ((userRole === 'user' || userRole === 'admin') && isOwnEntry);
                    const canDeleteThisEntry = userRole === 'superadmin' || ((userRole === 'user' || userRole === 'admin') && isOwnEntry);
                    
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs text-muted-foreground">{email}</TableCell>
                        <TableCell>
                          {canEditThisEntry ? (
                            <InlineText
                              value={r.description}
                              onChange={(v) => updateEntry(r.id, { description: v })}
                              placeholder="Describe work"
                            />
                          ) : (
                            <div className="text-sm py-1">{r.description || '—'}</div>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(r.tagIds || []).map(tid => {
                              const t = taskCodes.find(x => x.id === tid);
                              return t ? <Badge key={tid} variant="outline">{t.name}</Badge> : null;
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          {canEditThisEntry ? (
                            <InlineProject
                              value={r.projectId || ""}
                              onChange={(v) => updateEntry(r.id, { projectId: v })}
                              projects={matters}
                            />
                          ) : (
                            <div className="text-sm">{matters.find(m => m.id === r.projectId)?.name || '—'}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {canEditThisEntry ? (
                            <InlineNumber
                              value={r.decimalHours ?? (r.durationTenths / 10)}
                              onChange={(v) => updateEntry(r.id, { durationHours: v })}
                            />
                          ) : (
                            <div className="text-sm tabular-nums">{(r.decimalHours ?? (r.durationTenths / 10)).toFixed(1)}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {canDeleteThisEntry ? (
                            <Button variant="ghost" size="sm" onClick={() => deleteEntry(r.id)}>
                              <Trash2 className="size-4"/>
                            </Button>
                          ) : (
                            <div className="size-4"></div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      })}

      <MiniTracker
        userMap={userMap}
        running={running}
        desc={desc} setDesc={setDesc}
        projectId={projectId} setProjectId={setProjectId}
        matters={matters}
        selectedTagId={selectedTagId} setSelectedTagId={setSelectedTagId}
        taskCodes={taskCodes}
        elapsedDecimal={elapsedDecimal}
        startTimer={startTimer} stopTimer={stopTimer}
        selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        quickHours={quickHours} setQuickHours={setQuickHours}
        addQuickEntry={addQuickEntry}
      />

      {/* Superadmin pagination footer */}
      {isSuper && filtered.length > PAGE_SIZE && (
        <div className="hidden md:flex items-center justify-between border-t pt-4 mt-2" data-testid="track-pagination">
          <div className="text-sm text-muted-foreground" data-testid="track-pagination-info">
            Showing {pageStart + 1}–{Math.min(pageEnd, filtered.length)} of {filtered.length} entries
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="track-pagination-prev"
            >Previous</Button>
            <span className="text-sm text-muted-foreground tabular-nums" data-testid="track-pagination-page">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="track-pagination-next"
            >Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function InlineText({ value, onChange, placeholder }) { const [val, setVal] = useState(value || ""); useEffect(() => setVal(value || ""), [value]); return (<input value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => onChange(val)} placeholder={placeholder} className="w-full bg-transparent outline-none border-b border-transparent focus:border-border transition-colors text-sm py-1" />); }

function InlineProject({ value, onChange, projects }) { return (<Select value={(value || "none")} onValueChange={(v) => onChange(v === "none" ? "" : v)}> <SelectTrigger className="w-full max-w-sm"><SelectValue placeholder="Select Matter"/></SelectTrigger> <SelectContent> <SelectItem value="none">No matter</SelectItem> {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))} </SelectContent> </Select>); }

function InlineNumber({ value, onChange }) { const [val, setVal] = useState(String(value ?? "")); useEffect(() => setVal(String(value ?? "")), [value]); function commit() { const num = parseFloat(String(val).replace(/,/g, ".")); if (isNaN(num) || num < 0) return; onChange(Math.round(num * 10) / 10); } return (<input value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit} className="w-24 text-right bg-transparent outline-none border-b border-transparent focus:border-border transition-colors text-sm py-1 tabular-nums" inputMode="decimal" />); }

// Mini tracker for small screens (hoisted out of TrackPage to avoid being
// redefined on every render, which was destroying its DOM state on each parent re-render).
function MiniTracker({
  userMap, running,
  desc, setDesc,
  projectId, setProjectId,
  matters,
  selectedTagId, setSelectedTagId,
  taskCodes,
  elapsedDecimal, startTimer, stopTimer,
  selectedDate, setSelectedDate,
  quickHours, setQuickHours,
  addQuickEntry,
}) {
  return (
    <div className="md:hidden fixed bottom-4 right-4 z-50 w-[92%] max-w-sm rounded-lg border bg-card shadow-lg p-3">
      <div className="flex flex-col gap-2">
        <div className="text-xs text-muted-foreground">{userMap[running?.userId] || ''}</div>
        <Input value={desc} onChange={(e)=>setDesc(e.target.value)} placeholder="What are you working on?" />
        <Select value={(projectId || "none")} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select Matter"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No matter</SelectItem>
            {matters.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={(selectedTagId || "none")} onValueChange={(v) => setSelectedTagId(v === "none" ? "" : v)}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select Task Code"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No task code</SelectItem>
            {taskCodes.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold tabular-nums">{running ? `${elapsedDecimal.toFixed(2)}h` : "0.00h"}</div>
          {!running ? (<Button onClick={startTimer} className="gap-2 brand-red-bg text-white">Start</Button>) : (<Button variant="destructive" onClick={stopTimer} className="gap-2">Stop</Button>)}
        </div>
        <form onSubmit={addQuickEntry} className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground">Date</div>
          <input type="date" value={selectedDate} onChange={(e)=>setSelectedDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <Input value={quickHours} onChange={(e)=>setQuickHours(e.target.value)} placeholder="Hours e.g. 1.2" inputMode="decimal" />
          <Button type="submit" variant="secondary">Add Time</Button>
        </form>
      </div>
    </div>
  );
}

// Optimized description textarea with debouncing to prevent re-renders on every keystroke
const DescriptionTextarea = React.memo(({ value, onChange }) => {
  const [localValue, setLocalValue] = useState(value || "");
  const timeoutRef = React.useRef(null);
  
  // Update local value when prop changes (e.g., after saving or clearing)
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);
  
  // Handle typing with debounce
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout to sync after 300ms of no typing
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, 300);
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <textarea
      value={localValue}
      onChange={handleChange}
      placeholder="Describe your task."
      className="w-full rounded border px-3 py-3 text-base min-h-[96px]"
    />
  );
});

